import {setTitle} from '@github-ui/document-metadata'
import {hpcAttrs} from '@github-ui/hpc'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {SoftNavOnlyLink as Link, useLocation, useParams} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useNavigate} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {AlertIcon, BookmarkSlashIcon} from '@primer/octicons-react'
import {Button, Link as PrimerLink} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type ComponentProps, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {BUTTON_LABELS, LABELS} from '../../constants'
import {PreviewQueryProvider} from '../../contexts/PreviewQueryContext'
import {
  SAVED_VIEW_PIN_SOURCES,
  SAVED_VIEW_UPDATE_SOURCES,
  useSavedViewAnalytics,
} from '../../hooks/use-saved-view-analytics'
import {useSavedViewPinControls} from '../../hooks/use-saved-view-pin-controls'
import {useUpdateSavedViewMutation} from '../../hooks/use-update-saved-view-mutation'
import {SavedViewFetchError, savedViewQueryOptions, type SavedViewResponse} from '../../queries'
import {SAVED_VIEW_OPEN_SOURCES, type SavedViewOpenLocationState} from '../../saved-view-open-source'
import type {SavedViewIssuesRenderer, UpdateSavedViewResponse, UpdateSavedViewVariables} from '../../types'
import {SavedViewMutationErrorBanner} from '../dialogs/SavedViewMutationErrorBanner'
import styles from './RepositorySavedViewPage.module.css'
import {SavedViewEditView} from './SavedViewEditView'
import {SavedViewFilterBar} from './SavedViewFilterBar'
import {SavedViewHeader} from './SavedViewHeader'

interface RepositorySavedViewContentProps<TConfiguration> {
  IssuesRenderer: SavedViewIssuesRenderer
  pinEnabled: boolean
  perVisibilityPinCapEnabled: boolean
  renderSidebar: ComponentProps<typeof RepoIssuesPageLayout>['renderSidebar']
  renderCreateIssueButton: () => React.ReactNode
  /**
   * Controls shown before the filter bar. As a function, it is called with whether Save is
   * pending, so a control that changes what Save sends can stay off until Save settles.
   */
  filterBarActions?: React.ReactNode | ((state: {isSaving: boolean}) => React.ReactNode)
  /** The view the page was served with, so the page renders before `/:id.json` answers. */
  initialData?: SavedViewResponse
  /** When `initialData` was fetched. */
  initialDataUpdatedAt?: number
  /** Host-owned configuration the view is shown with, sent with Save and with Save as new view. */
  configuration?: TConfiguration
  /** Whether `configuration` differs from the saved one. Counts as an unsaved change. */
  isConfigurationDirty?: boolean
  /**
   * Handler that is called with the response and the request when Save succeeds. When set, the
   * host clears the previewed query and its own URL overrides; otherwise the previewed query is
   * cleared from the URL.
   */
  onSaveSuccess?: (
    response: UpdateSavedViewResponse<TConfiguration>,
    sent: UpdateSavedViewVariables<TConfiguration>,
  ) => void
  /**
   * Handler that is called when an edit to the view is saved. When set, the host clears the
   * previewed query; otherwise the previewed query is cleared from the URL.
   */
  onEditSuccess?: () => void
  /**
   * Maximum width of the page.
   * @default 'xlarge'
   */
  width?: ComponentProps<typeof RepoIssuesPageLayout>['width']
  /**
   * Whether the host is loading the page for a new URL. Save and its retry stay off until it
   * settles, so Save cannot send a configuration the page has not shown yet.
   * @default false
   */
  isNavigating?: boolean
}

export function RepositorySavedViewContent<TConfiguration = never>({
  IssuesRenderer,
  pinEnabled,
  perVisibilityPinCapEnabled,
  renderSidebar,
  renderCreateIssueButton,
  filterBarActions,
  initialData,
  initialDataUpdatedAt,
  configuration,
  isConfigurationDirty = false,
  onSaveSuccess,
  onEditSuccess,
  width,
  isNavigating = false,
}: RepositorySavedViewContentProps<TConfiguration>) {
  const {owner = '', repo = '', id = ''} = useParams<{owner: string; repo: string; id: string}>()
  const viewsPath = `/${owner}/${repo}/issues/views`
  const {pathname, search, state} = useLocation()
  const navigate = useNavigate()

  const {data, error, isError, isFetching, isLoading, refetch} = useQuery(
    savedViewQueryOptions({owner, repo, id, initialData, initialDataUpdatedAt}),
  )

  const savedView = data?.savedView
  const viewerCanManageViews = data?.viewerCanManageViews ?? false
  const pinnedViewsEnabled = pinEnabled
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views_team_vs_personal uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const visibilityEnabled = useFeatureFlag('issue_pinned_views_team_vs_personal')

  const {trackViewOpened, trackEditOpened, trackViewUpdated} = useSavedViewAnalytics()
  const {currentUser} = useUser()
  const openSource = (state as SavedViewOpenLocationState | null)?.savedViewOpenSource ?? SAVED_VIEW_OPEN_SOURCES.direct
  const openedByCreator =
    savedView != null && currentUser?.login != null && currentUser.login === savedView.creatorLogin
  const trackedOpenIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (savedView === undefined) return
    const viewMetadata =
      typeof savedView.viewId === 'number' && Number.isFinite(savedView.viewId)
        ? {viewId: savedView.viewId, placementOwnerType: 'repository' as const, searchType: 'issues' as const}
        : undefined
    if (viewMetadata === undefined && isFetching) return
    if (trackedOpenIdRef.current === String(savedView.id)) return
    trackedOpenIdRef.current = String(savedView.id)
    trackViewOpened(savedView.id, savedView.visibility, openSource, openedByCreator, viewMetadata)
  }, [isFetching, savedView, trackViewOpened, openSource, openedByCreator])

  useEffect(() => {
    if (savedView?.name) {
      setTitle(LABELS.documentTitleForView(savedView.name))
    }
  }, [savedView?.name])

  const urlQuery = useMemo(() => new URLSearchParams(search).get('q'), [search])
  const effectiveQuery = urlQuery ?? savedView?.query ?? ''

  const updateUrlQuery = useCallback(
    (nextQuery: string) => {
      const trimmed = nextQuery.trim()
      const params = new URLSearchParams(search)
      if (trimmed) {
        params.set('q', trimmed)
      } else {
        params.delete('q')
      }
      // Reset pagination when the query changes; the new result set may be shorter.
      params.delete('page')
      const query = params.toString()
      navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
    },
    [navigate, pathname, search],
  )

  const [isEditing, setIsEditing] = useState(false)

  const handleStartEditing = useCallback(() => {
    if (savedView !== undefined) {
      trackEditOpened(savedView.id, savedView.visibility, openedByCreator)
    }
    setIsEditing(true)
  }, [savedView, trackEditOpened, openedByCreator])

  const handleExitEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSaveSuccess = useCallback(() => {
    if (onEditSuccess) {
      onEditSuccess()
    } else if (urlQuery !== null) {
      updateUrlQuery('')
    }
    setIsEditing(false)
  }, [onEditSuccess, updateUrlQuery, urlQuery])

  const {
    mutate: updateView,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateSavedViewMutation<TConfiguration>({owner, repo})

  const {pinControls, pinErrorMessage, retryPin, isPinRetrying} = useSavedViewPinControls({
    owner,
    repo,
    enabled: pinnedViewsEnabled,
    source: SAVED_VIEW_PIN_SOURCES.individualViewActionsMenu,
    perVisibilityPinCapEnabled,
  })

  const handleSaveChanges = useCallback(() => {
    if (!savedView || isNavigating) return
    const trimmedQuery = effectiveQuery.trim()
    updateView(
      {
        id: savedView.id,
        name: savedView.name,
        query: trimmedQuery,
        description: savedView.description ?? undefined,
        icon: savedView.icon,
        color: savedView.color,
        ...(configuration !== undefined ? {configuration} : {}),
      },
      {
        onSuccess: (response, sent) => {
          trackViewUpdated(savedView.id, savedView.visibility, SAVED_VIEW_UPDATE_SOURCES.query)
          if (onSaveSuccess) {
            onSaveSuccess(response, sent)
          } else {
            updateUrlQuery('')
          }
        },
      },
    )
  }, [
    configuration,
    effectiveQuery,
    isNavigating,
    onSaveSuccess,
    savedView,
    trackViewUpdated,
    updateUrlQuery,
    updateView,
  ])

  const readPreviewQueryValue = useMemo(
    () => ({currentQuery: effectiveQuery, onQueryChange: updateUrlQuery}),
    [effectiveQuery, updateUrlQuery],
  )

  const isNotFound = error instanceof SavedViewFetchError && error.status === 404
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const notFoundBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <BookmarkSlashIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.notFoundTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.notFoundDescription}</Blankslate.Description>
      <Button as={Link} to={viewsPath} variant="primary" className={styles.blankslateAction}>
        {LABELS.views.backToViews}
      </Button>
    </Blankslate>
  )

  const errorBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.loadErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.loadErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => refetch()}>{BUTTON_LABELS.tryAgain}</Blankslate.PrimaryAction>
      <PrimerLink as={Link} to={viewsPath}>
        {LABELS.views.backToViews}
      </PrimerLink>
    </Blankslate>
  )

  const fetchErrorBlankslate = isNotFound ? notFoundBlankslate : errorBlankslate

  const renderErrorFallback = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.displayErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.displayErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => window.location.reload()}>
        {BUTTON_LABELS.reloadPage}
      </Blankslate.PrimaryAction>
    </Blankslate>
  )

  return (
    <RepoIssuesPageLayout
      dialogTitle={LABELS.viewsTitle}
      renderSidebar={renderSidebar}
      showNarrowSidebarTrigger
      width={width}
    >
      <div className={sidebarLayoutEnabled ? styles.contentInset : 'mt-3'} {...hpcAttrs()} aria-busy={isLoading}>
        <ErrorBoundary boundaryName="RepositorySavedViewPage" fallback={renderErrorFallback}>
          {isError ? (
            fetchErrorBlankslate
          ) : savedView ? (
            isEditing ? (
              <SavedViewEditView
                key={savedView.id}
                savedView={savedView}
                owner={owner}
                repo={repo}
                onSaved={handleSaveSuccess}
                onCancel={handleExitEditing}
                visibilityEnabled={visibilityEnabled}
                IssuesRenderer={IssuesRenderer}
              />
            ) : (
              <>
                <div className={sidebarLayoutEnabled ? styles.compactHeader : undefined}>
                  <SavedViewHeader
                    savedView={savedView}
                    owner={owner}
                    repo={repo}
                    viewsPath={viewsPath}
                    onStartEditing={handleStartEditing}
                    canManage={viewerCanManageViews}
                    pinnedViewsEnabled={pinnedViewsEnabled}
                    visibilityEnabled={visibilityEnabled}
                    pinControls={pinControls}
                    renderCreateIssueButton={renderCreateIssueButton}
                    actionsPlacement={sidebarLayoutEnabled ? 'title' : 'end'}
                    withFilterSpacing={sidebarLayoutEnabled}
                  />
                </div>
                <div className={styles.filterBarWrapper} data-compact-header={sidebarLayoutEnabled ? '' : undefined}>
                  <SavedViewMutationErrorBanner
                    message={pinErrorMessage}
                    onRetry={retryPin}
                    isRetrying={isPinRetrying}
                    focusOnMount={false}
                  />
                  <SavedViewMutationErrorBanner
                    message={updateError ? LABELS.views.updateError : null}
                    onRetry={handleSaveChanges}
                    isRetrying={isUpdating}
                    retryDisabled={isNavigating}
                  />
                  <SavedViewFilterBar<TConfiguration>
                    owner={owner}
                    repo={repo}
                    savedView={savedView}
                    currentQuery={effectiveQuery}
                    onQueryChange={updateUrlQuery}
                    onSaveChanges={handleSaveChanges}
                    isSaving={isUpdating}
                    isNavigating={isNavigating}
                    isConfigurationDirty={isConfigurationDirty}
                    configuration={configuration}
                    canManage={viewerCanManageViews}
                    leadingActions={
                      typeof filterBarActions === 'function'
                        ? filterBarActions({isSaving: isUpdating})
                        : filterBarActions
                    }
                  />
                </div>
                <div className={styles.middlePaneGrid} data-compact-header={sidebarLayoutEnabled ? '' : undefined}>
                  <PreviewQueryProvider value={readPreviewQueryValue}>
                    <IssuesRenderer owner={owner} repo={repo} query={effectiveQuery} preheatEnabled />
                  </PreviewQueryProvider>
                </div>
              </>
            )
          ) : null}
        </ErrorBoundary>
      </div>
    </RepoIssuesPageLayout>
  )
}
